import { ReactNode, useState } from "react";
import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Menu, Modal, Row, Typography } from "antd";
import clsx from "clsx";
import {
  DocFolderIcon,
  EmailPushIcon,
  DownloadIcon,
  LinkOneIcon,
  SearchIcon,
  VectorIcon,
  WriteIcon,
} from "@/shared/assets/svg/icons";
import { useCoordinator } from "@/shared/lib/hooks";
import { routes } from "@/shared/lib/react-router/routes";
import { Chipper } from "@/shared/ui/chipper";
import { useNavigate } from "@shared/lib/react-router";
import { SupervisionCardProps } from "./types";
import "./supervision-card.scss";

export const SupervisionCard = ({
  title,
  textNumber,
  chippers,
  table,
  variant = "default",
  className,
  onAddToPackage,
  showSignButton = false,
  onLinkMessage,
  onTitleClick,
}: SupervisionCardProps) => {
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const { projectId, supervisionId } = useCoordinator();

  const navigate = useNavigate();

  const handleToggleTable = () => {
    setIsTableExpanded(!isTableExpanded);
  };

  const handleTitleClick = () => {
  };

  const handleSignButtonClick = () => {
    setIsSignModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsSignModalOpen(false);
  };

  const handleGoToSettings = () => {
    navigate(routes.supervision.adminSettings(projectId, supervisionId));
    setIsSignModalOpen(false);
  };

  const cardClassName = clsx("supervision-card", className);
  const vectorIconClassName = clsx(
    "supervision-card__vector-icon",
    !isTableExpanded && "rotated",
  );
  const tableClassName = clsx(
    "supervision-card__table",
    !isTableExpanded ? "collapsed" : "expanded",
  );

  const menu = (
    <Menu>
      <Menu.Item key="1" className="supervision-card__menu-item">
        <div className="supervision-card__menu-item">
          <div className="supervision-card__menu-item-icon">
            <WriteIcon />
          </div>
          Сменить подписанта
        </div>
      </Menu.Item>
      <Menu.Item
        key="2"
        className="supervision-card__menu-item"
        onClick={onAddToPackage}
      >
        <div className="supervision-card__menu-item">
          <div className="supervision-card__menu-item-icon">
            <DocFolderIcon />
          </div>
          Добавить в пакет документов
        </div>
      </Menu.Item>
      <Menu.Item key="3" className="supervision-card__menu-item">
        <div className="supervision-card__menu-item">
          <div className="supervision-card__menu-item-icon">
            <DownloadIcon />
          </div>
          Скачать все файлы
        </div>
      </Menu.Item>
      <Menu.Item key="4" className="supervision-card__menu-item">
        <div className="supervision-card__menu-item">
          <div className="supervision-card__menu-item-icon">
            <SearchIcon />
          </div>
          Открыть историю изменений
        </div>
      </Menu.Item>
      <Menu.Item
        key="5"
        className="supervision-card__menu-item"
        onClick={onLinkMessage}
      >
        <div className="supervision-card__menu-item">
          <div className="supervision-card__menu-item-icon">
            <LinkOneIcon />
          </div>
          Связать сообщение
        </div>
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={cardClassName}>
      <div className="supervision-card__header">
        <div className="supervision-card__info">
          <div className={vectorIconClassName} onClick={handleToggleTable}>
            <VectorIcon />
          </div>
          <EmailPushIcon />
          {chippers.map((chipper) => (
            <Chipper
              key={`chipper-${chipper.text}`}
              backgroundColor={
                chipper.backgroundColor || "rgba(119, 119, 119, 1)"
              }
            >
              {chipper.text}
            </Chipper>
          ))}
        </div>
        {menu && (
          <Dropdown overlay={menu} trigger={["hover"]}>
            <Button
              shape="circle"
              icon={(<EllipsisOutlined />) as ReactNode}
              className="supervision-card__menu-button"
            />
          </Dropdown>
        )}
      </div>

      <div className="supervision-card__title-row">
        <Typography.Title
          level={5}
          className={clsx(
            "supervision-card__title",
            variant === "dangerous" && "supervision-card__title--dangerous",
          )}
          onClick={onTitleClick || handleTitleClick}
        >
          {title}
        </Typography.Title>
        {textNumber && (
          <div className="supervision-card__text-number">№{textNumber}</div>
        )}
      </div>

      {table && table.length > 0 && (
        <div className={tableClassName}>
          <Row>
            <Col span={6}>
              <Typography.Text className="supervision-card__table-header-text">
                Отправитель
              </Typography.Text>
            </Col>
            <Col span={6}>
              <Typography.Text className="supervision-card__table-header-text">
                Получатель
              </Typography.Text>
            </Col>
            <Col span={6}>
              <Typography.Text className="supervision-card__table-header-text">
                Документ подписан
              </Typography.Text>
            </Col>
            <Col span={6}>
              <Typography.Text className="supervision-card__table-header-text">
                Письмо отправлено
              </Typography.Text>
            </Col>
          </Row>

          {table.map((row) => (
            <Row key={`table-row-${row.sender}-${row.receiver}`}>
              <Col span={6}>
                <Typography.Text className="supervision-card__table-cell-text">
                  {row.sender}
                </Typography.Text>
              </Col>
              <Col span={6}>
                <Typography.Text className="supervision-card__table-cell-text">
                  {row.receiver}
                </Typography.Text>
              </Col>
              <Col span={6}>
                <div className="supervision-card__date-time">
                  <Typography.Text className="supervision-card__date">
                    {row.documentSigned.date}
                  </Typography.Text>
                  <Typography.Text className="supervision-card__time">
                    {row.documentSigned.time}
                  </Typography.Text>
                </div>
              </Col>
              <Col span={6}>
                <div className="supervision-card__date-time">
                  <Typography.Text className="supervision-card__date">
                    {row.letterSent.date}
                  </Typography.Text>
                  <Typography.Text className="supervision-card__time">
                    {row.letterSent.time}
                  </Typography.Text>
                </div>
              </Col>
            </Row>
          ))}
        </div>
      )}

      {showSignButton && (
        <div className="supervision-card__sign-section">
          <Button 
            type="primary" 
            onClick={handleSignButtonClick}
            className="supervision-card__sign-button"
          >
            Передать на подпись
          </Button>
        </div>
      )}

      <Modal
        title="Выбор подписанта для сообщения"
        open={isSignModalOpen}
        onCancel={handleModalCancel}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            Отменить
          </Button>,
          <Button key="settings" type="primary" onClick={handleGoToSettings}>
            Перейти в настройки
          </Button>,
        ]}
        width={600}
      >
        <div className="supervision-card__modal-notice">
          <Typography.Text style={{ color: "rgba(255, 152, 0, 1)", fontSize: 14 }}>
            В настройках администрирования нет подписантов.
          </Typography.Text>
        </div>
        <Typography.Text style={{ fontSize: 14 }}>
          Добавьте хотя бы одного подписанта, для передачи на подписание.
        </Typography.Text>
      </Modal>
    </div>
  );
};
